// 游客标记 + 延迟丢弃标志 + guest 期删除证据（跨窗口）。
//
// 零依赖存储层：被 outbox 的 enqueue 短路**同步**调用，绝不 import store / session /
// supabase（防 store→outbox→auth→store 循环依赖，也防把 supabase 客户端拉进
// store/outbox 的测试模块图）。session 腿由 lib/auth/session 在会话变化时经
// _setSessionActiveForGuestGate 注入（authed 恒入队的第一优先级判据）。
//
// isGuestActive 的失败方向已裁定（设计 v4 §3.1）：
//   - fail-closed（未知 → 不入队）错 = authed 编辑永久静默不同步（snapshot 替换时被物理删除）
//   - fail-open（未知 → 入队）错 = guest 漏几条 op，由 guest 生命周期的 outbox 自愈清理兜底
// 因此任何「读不出 / 降级 / 未注入」都必须落在 false（入队）一侧。
//
// 唯一允许的 import：@doneline/shared 纯常量（叶子包，无副作用，不破坏零依赖约束）。
import { ENTITY_KINDS } from '@doneline/shared';

const GUEST_KEY = 'doneline-guest-v1';
const DISCARD_KEY = 'doneline-guest-discard-v1';
const TOMBSTONES_KEY = 'doneline-guest-tombstones-v1';

export interface GuestTombstone {
  kind: string;
  id: string;
}

let sessionActive = false;

/** 由 lib/auth/session 在每次会话变化时注入（emit 路径）。 */
export function _setSessionActiveForGuestGate(active: boolean): void {
  sessionActive = active;
}

function safeGet(key: string): { ok: boolean; value: string | null } {
  try {
    return { ok: true, value: localStorage.getItem(key) };
  } catch {
    return { ok: false, value: null };
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // 移除失败：留给下次清理路径重试；读侧判据（owner 优先）保证残留无害
  }
}

// ---------- guest marker ----------

export function hasGuestMarker(): boolean {
  return safeGet(GUEST_KEY).value != null;
}

/** marker 键自身读降级（隐私模式禁读等）。降级时不判 guest（fail-closed 到登录墙方向）。 */
export function isGuestMarkerDegraded(): boolean {
  return !safeGet(GUEST_KEY).ok;
}

/** 写失败返回 false —— 调用方（跳过按钮）提示重试、不进 app。 */
export function setGuestMarker(): boolean {
  try {
    localStorage.setItem(GUEST_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

export function clearGuestMarker(): void {
  safeRemove(GUEST_KEY);
}

// ---------- pending discard（延迟丢弃：登录表单写入，engine startSync 顶部消费） ----------

export function hasPendingDiscard(): boolean {
  return safeGet(DISCARD_KEY).value != null;
}

export function setPendingDiscard(): void {
  try {
    localStorage.setItem(DISCARD_KEY, '1');
  } catch {
    // 写失败 = 用户的丢弃意图没被记录，登录后会走默认合并 —— 数据不丢，方向安全
  }
}

export function clearPendingDiscard(): void {
  safeRemove(DISCARD_KEY);
}

// ---------- guest 期删除证据（web 跨窗口并集合并的删除裁决用；adoption/discard/wipe 时清空） ----------

// 形状校验（v4.1-3）：kind 必须是协议实体枚举、id 必须是 UUID（task_tag 允许 "uuid:uuid"
// 复合形态）。localStorage 是可被同源脚本/用户改写的不可信输入——只做 typeof string 时，
// 垃圾行会被 adoption 当删除证据带上行（buildAdoptionOps 侧还有一道，但入口就该拦）。
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidTombstone(t: unknown): t is GuestTombstone {
  if (typeof t !== 'object' || t === null) return false;
  const { kind, id } = t as GuestTombstone;
  if (typeof kind !== 'string' || typeof id !== 'string') return false;
  if (!(ENTITY_KINDS as readonly string[]).includes(kind)) return false;
  const segs = id.split(':');
  if (segs.length === 2) return kind === 'task_tag' && segs.every((s) => UUID_RE.test(s));
  return segs.length === 1 && UUID_RE.test(id);
}

function readTombstones(): GuestTombstone[] {
  const raw = safeGet(TOMBSTONES_KEY).value;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidTombstone);
  } catch {
    return [];
  }
}

export function getGuestTombstones(): GuestTombstone[] {
  return readTombstones();
}

// 容量上限（v4.1-3）：长期 guest 的删除无界增长会打爆与 store 快照共享的 localStorage
// 配额；超限 FIFO 丢最旧（丢的只是跨窗口证据，单窗口 store 内已删，可接受降级）。
const TOMBSTONES_CAP = 5000;

export function recordGuestTombstone(kind: string, id: string): void {
  // read-modify-write：与 outbox 同款跨窗口语义（两个 webview 都可能删）
  const list = readTombstones();
  if (list.some((t) => t.kind === kind && t.id === id)) return;
  const next = [...list, { kind, id }];
  const capped = next.length > TOMBSTONES_CAP ? next.slice(next.length - TOMBSTONES_CAP) : next;
  try {
    localStorage.setItem(TOMBSTONES_KEY, JSON.stringify(capped));
  } catch {
    // 写失败：该删除在另一窗口可能复活（跨窗口证据缺失），但单窗口 store 内已删 —— 可接受降级
  }
}

export function clearGuestTombstones(): void {
  safeRemove(TOMBSTONES_KEY);
}

// ---------- enqueue 短路门 ----------

export function isGuestActive(): boolean {
  if (sessionActive) return false; // authed 恒入队（第一优先级，不读 marker）
  return hasGuestMarker() && !isGuestMarkerDegraded();
}
